import {DepositPayloadType, DepositResponse, EProtectResponse} from '../types';
import {getLocation} from '../utils';
import ServiceClient from '../utils/ServiceClient';

type DepositErrorLike = {
  code?: string, message?: string, request?: unknown, response?: { data?: { message?: unknown } }
};

type CardDepositInput = {
  amountCents: number, saveCard: boolean, cardNumber: string, cvc: string, expirationDate: string,
  tokenizeCard: (number: string, cvc: string) => Promise<EProtectResponse>, clearSensitiveFields: () => void
};

type CardDepositResult = { eProtectResponse: EProtectResponse, depositResponse: DepositResponse | undefined };

type CardDepositPhase = 'validation' | 'location' | 'tokenization' | 'authorization' | 'deposit';

const defaultDepositErrorMessage = "We couldn't complete your deposit. Please try again or contact support.";

export class CardDepositSubmissionError extends Error {
  constructor(public readonly cause: unknown, public readonly phase: CardDepositPhase, public readonly depositRequestStarted: boolean) {
    super('Card deposit submission failed.');
    this.name = 'CardDepositSubmissionError';
  }
}

export const getCardDepositErrorCause = (error: unknown) => {
  return error instanceof CardDepositSubmissionError ? error.cause : error;
};

export const isLocalCardDepositError = (error: unknown) => {
  if (!(error instanceof CardDepositSubmissionError)) return false;

  const cause = getCardDepositErrorCause(error);
  const typedError = cause as DepositErrorLike;
  const canShowDirectMessage = error.phase === 'validation' || error.phase === 'location' || error.phase === 'tokenization';

  return canShowDirectMessage && cause instanceof Error && !typedError.response && !typedError.request && typedError.code !== 'ECONNABORTED';
};

export const getCardDepositErrorInfo = (error: unknown) => {
  const cause = getCardDepositErrorCause(error);
  const typedError = cause as DepositErrorLike;
  const responseMessage = typedError.response?.data?.message;

  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return {message: defaultDepositErrorMessage, serverMessage: responseMessage.trim()};
  }

  const directMessage = typeof typedError.message === 'string' ? typedError.message.trim() : '';

  if (isLocalCardDepositError(error) && directMessage) {
    return {message: directMessage, serverMessage: ''};
  }

  if (directMessage && !typedError.request && typedError.code !== 'ECONNABORTED') {
    return {message: defaultDepositErrorMessage, serverMessage: directMessage};
  }

  return {message: defaultDepositErrorMessage, serverMessage: ''};
};

export const isAmbiguousCardDepositError = (error: unknown) => {
  return error instanceof CardDepositSubmissionError && error.depositRequestStarted;
};

export const submitCardDeposit = async (input: CardDepositInput): Promise<CardDepositResult> => {
  let depositRequestStarted = false;
  let phase: CardDepositPhase = 'validation';

  try {
    if (!Number.isSafeInteger(input.amountCents) || input.amountCents <= 0) {
      throw new Error('Enter a valid deposit amount before adding card information.');
    }

    phase = 'location';
    const location = await getLocation();
    if (!location) throw new Error('Permission to access the location was denied.');

    phase = 'tokenization';
    const eProtectResponse = await input.tokenizeCard(input.cardNumber, input.cvc).finally(input.clearSensitiveFields);
    const data: DepositPayloadType = {
      amount: input.amountCents, saveNewCard: input.saveCard, cardType: eProtectResponse.type,
      orderId: eProtectResponse.orderId, merchantTxId: eProtectResponse.id, reportGroup: eProtectResponse.reportGroup,
      vendorTxId: eProtectResponse.vantivTxnId, payPageRegistrationId: eProtectResponse.paypageRegistrationId,
      ...(input.saveCard ? {expirationDate: input.expirationDate} : {}),
      location: {latitude: `${location.coords.latitude}`, longitude: `${location.coords.longitude}`}
    };

    phase = 'authorization';
    await ServiceClient.getPayGateToken();
    phase = 'deposit';
    depositRequestStarted = true;
    // TODO(SMG-3487): Send `Idempotency-Key` once the PayGate deposit API supports it.
    const depositResponse = await ServiceClient.deposit(data);

    return {eProtectResponse, depositResponse};

  } catch (cause) {
    throw new CardDepositSubmissionError(cause, phase, depositRequestStarted);
  }
};
